/**
 * HOOK: makes a user-triggered action single-flight — while one invocation is in flight, another
 * with the same key is ignored (resolves `undefined` once the running one settles) instead of
 * repeating the write or send. See "User-triggered actions are single-flight" in REACT_CONVENTIONS.md.
 */
import { useCallback, useState } from 'react';

import { createStore, ReactiveStore } from '../stores/create-store';
import { useLatestRef } from './use-latest-ref';

// The running flights by key, each mapped to a promise that settles when it ends. A module-level scope
// outlives the component, so a remount can't start a second copy of work that is still running.
export type SingleFlightScope = ReactiveStore<ReadonlyMap<string, Promise<void>>>;

export const createSingleFlightScope = (): SingleFlightScope => createStore<ReadonlyMap<string, Promise<void>>>(new Map());

const WHOLE_ACTION = '';

interface ISingleFlightOptions<Args extends unknown[]> {
  // Invocations with different keys run side by side, e.g. one per player.
  key?: (...args: Args) => string;
  // Must be module-level; defaults to a scope private to this component.
  scope?: SingleFlightScope;
  onBlocked?: (...args: Args) => void;
}

export interface ISingleFlight<Args extends unknown[], Result> {
  run: (...args: Args) => Promise<Result | undefined>;
  inFlight: boolean;
  isInFlight: (key?: string) => boolean;
}

export const useSingleFlight = <Args extends unknown[], Result>(
  action: (...args: Args) => Result | Promise<Result>,
  options: ISingleFlightOptions<Args> = {},
): ISingleFlight<Args, Result> => {
  const [ownScope] = useState(createSingleFlightScope);
  const [useFlights, getFlights, setFlights] = options.scope || ownScope;
  const flights = useFlights();
  const actionRef = useLatestRef(action);
  const optionsRef = useLatestRef(options);

  // The store is read and written synchronously, so a second click in the same tick sees the first.
  const run = useCallback(async (...args: Args): Promise<Result | undefined> => {
    const { key = () => WHOLE_ACTION, onBlocked } = optionsRef.current;
    const flightKey = key(...args);
    const running = getFlights().get(flightKey);

    if (running) {
      if (onBlocked) onBlocked(...args);
      // Holds a Formik submit in `isSubmitting` until the real flight ends; its result and error stay with its caller.
      await running;
      return undefined;
    }

    let settle = () => {};
    setFlights(new Map(getFlights()).set(flightKey, new Promise<void>(resolve => { settle = resolve; })));
    try {
      return await actionRef.current(...args);
    } finally {
      settle();
      const remaining = new Map(getFlights());
      remaining.delete(flightKey);
      setFlights(remaining);
    }
  }, [getFlights, setFlights, actionRef, optionsRef]);

  const isInFlight = useCallback((key: string = WHOLE_ACTION) => flights.has(key), [flights]);

  return { run, inFlight: flights.size > 0, isInFlight };
};
